export const medicineVariantFields = ['dosageForm', 'strength', 'packTypes', 'avgPriceOfOriginator', 'sellingPrice', 'discount', 'vipSellingPrice', 'weight', 'showInFront', 'companyPatientSellingPriceUpdated', 'companyPatientSellingPriceOld', 'companyMRP', 'minimumOrderQty'];

export function medicineEditorSeed(seed = {}, language = 'en') {
  const value = seed.content || {}, supplied = seed.variants || value.variantInfo || [];
  const byId = new Map(supplied.map(row => [String(row.fixedVariantId), row]));
  const ordered = (value.variants || []).map(row => byId.get(String(row.variantId))).filter(Boolean);
  const variants = ordered.length ? ordered : supplied;
  const result = {
    visible: value.visible ?? true, availability: value.availability === null ? '' : value.availability ?? 0,
    genericBrand: value.genericBrand || '', generic: value.generic || '', originator: value.originator || '',
    companyId: value.companyId || '', subCatId: (value.subCats || []).map(row => row.subCatId),
    englishVariantId: seed.englishVariantId || value.medicineDetectorId || '',
    prescribed: value.prescribed ?? true, topSeller: Boolean(value.topSeller), dealOfTheWeek: Boolean(value.dealOfTheWeek), isNew: Boolean(value.isNew ?? value.new),
    stock: Number(value.stock) > 0, stockCount: value.stock || 0, images: (value.image || []).map(row => ({ ...row })), videos: (value.video || []).map(row => ({ ...row })),
    metaKeyword: value.metaKeyword || '', pageTitle: value.pageTitle || '', metaTitle: value.metaTitle || '', metaDesc: value.metaDesc || '',
    tags: (value.tags || []).map(row => typeof row === 'string' ? row : row.tagsId || row.tagId || row.fixedId || row.tagDetectorId).filter(Boolean),
    additionalSectionList: (value.additionalSections || []).map(row => ({ ...row })), addVariantSection: variants.length,
    variantArr: seed.existing ? variants.map(row => row.fixedVariantId) : [],
    variantDetectorId: language === 'en' ? [] : variants.map(row => row.variantDetectorId || (seed.existing ? '' : row.fixedVariantId) || ''),
    revision: value._revision,
  };
  for (const field of medicineVariantFields) result[field] = variants.map(row => row[field] ?? (field === 'showInFront' ? true : ''));
  return result;
}

export function medicineDraftStorageKey({ actorId, scope, language }) {
  return `mfw.medicineDraft.v1:${encodeURIComponent(actorId || 'session')}:${encodeURIComponent(scope)}:${language}`;
}

export function mergeMedicineReviewDraft({ seedState, cachedState, selectedFields = [], language, generated = true }) {
  const result = { ...seedState, ...cachedState };
  // The English identity is bound to the generation. Localized medicine names
  // remain manually editable, and the cached revision is never silently rebased.
  if (language === 'en') for (const field of ['generic','genericBrand']) result[field] = seedState[field];
  result.englishVariantId = seedState.englishVariantId;
  const selected = generated ? selectedFields : [];
  for (const field of selected) if (['metaKeyword','pageTitle','metaTitle','metaDesc'].includes(field)) result[field] = seedState[field];
  const sections = new Map((seedState.additionalSectionList || []).map(row => [row.sectionName, row]));
  result.additionalSectionList = (cachedState.additionalSectionList || []).map(row => selected.includes(`section:${row.sectionName}`) ? { ...row, content: sections.get(row.sectionName)?.content ?? row.content } : { ...row });
  for (const [name, row] of sections) if (selected.includes(`section:${name}`) && !result.additionalSectionList.some(item => item.sectionName === name)) result.additionalSectionList.push({ ...row });
  return result;
}

export function refreshMedicineDraftLinks(cachedState, seedState) {
  const normalize = value => String(value ?? '').normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
  const tuple = (state, index) => ['dosageForm','strength','packTypes'].map(field => normalize(state[field]?.[index])).join('\u0000');
  const byTuple = new Map();
  for (let index = 0; index < (seedState.dosageForm || []).length; index++) {
    const id = seedState.variantDetectorId?.[index]; if (!id) continue;
    const key = tuple(seedState, index); byTuple.set(key, [...(byTuple.get(key) || []), id]);
  }
  return { ...cachedState, englishVariantId: cachedState.englishVariantId || seedState.englishVariantId,
    variantDetectorId: (cachedState.dosageForm || []).map((_, index) => {
      if (cachedState.variantDetectorId?.[index]) return cachedState.variantDetectorId[index];
      const matches = byTuple.get(tuple(cachedState, index)) || [];
      return matches.length === 1 ? matches[0] : '';
    }),
  };
}

export function serializableMedicineDraft(state) {
  const isUpload = value => value && typeof value === 'object' && typeof value.name === 'string' && typeof value.size === 'number';
  const mediaNotPersisted = [...(state.images || []), ...(state.videos || [])].some(row => isUpload(row.file));
  const safe = { ...state, images: (state.images || []).filter(row => !isUpload(row.file)), videos: (state.videos || []).filter(row => !isUpload(row.file)) };
  return { state: JSON.parse(JSON.stringify(safe, (_, value) => isUpload(value) ? undefined : value)), mediaNotPersisted };
}
